import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { random, sortBy } from 'lodash-es';
import { useMemo, useRef, useState } from 'preact/hooks';

import { useOnClickOutside } from '@/hooks';
import { useSelector } from '@/redux/helper';

import Card from './Card';
import HabitatIcon from './HabitatIcon';

interface LiveChannelsBarProps {
  skipHabitatId?: string;
}

const LiveChannelsBar = ({ skipHabitatId }: LiveChannelsBarProps) => {
  const allHabitats: any[] = useSelector((state) => state.allHabitats);
  const [expand, setExpand] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  /// Update list order only habitats live talk or online state changes
  const habitatsDataJson = useMemo(
    () =>
      JSON.stringify(
        sortBy(
          (allHabitats ?? []).map(({ _id, liveTalk, online }) => ({ _id, liveTalk, online })),
          '_id',
        ),
      ),
    [allHabitats],
  );

  const list = useMemo(
    () =>
      allHabitats
        .filter(({ online, _id }) => online && _id !== skipHabitatId)
        .sort((firstHabitat, secondHabitat) => {
          const firstIsLive = firstHabitat.liveTalk ? Number.MAX_SAFE_INTEGER : random(0, Number.MAX_SAFE_INTEGER - 1);
          const secondIsLive = secondHabitat.liveTalk ? Number.MAX_SAFE_INTEGER : random(0, Number.MAX_SAFE_INTEGER - 1);
          return secondIsLive - firstIsLive;
        }),
    // habitatsDataJson depends on allHabitats already
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [habitatsDataJson, skipHabitatId],
  );

  useOnClickOutside(ref, () => setExpand(false));

  return (
    <div ref={ref} className="absolute inset-0 shadow-md">
      <div className="flex h-full flex-col">
        <button
          type="button"
          className="preflight preflight-button flex w-full items-center justify-center gap-1 p-2 text-xs font-bold"
          onClick={() => setExpand(true)}
        >
          <span>Live</span>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
        <ul className="preflight preflight-ul customScrollBar light flex grow flex-col gap-2 pb-4">
          {list.map((habitat) => (
            <li key={habitat._id}>
              <HabitatIcon habitat={habitat} />
            </li>
          ))}
        </ul>
      </div>

      <div
        aria-hidden={!expand}
        className="absolute inset-y-0 left-0 z-10 flex translate-x-0 flex-col bg-white shadow-1 shadow-black/15 transition-transform aria-hidden:-translate-x-full aria-hidden:shadow-none"
      >
        <div className="flex items-center justify-between p-2">
          <div className="flex items-center gap-1">
            <h4 className="preflight preflight-h4 text-xs font-bold">Live</h4>
            <a href="/map" className="preflight preflight-a flex items-center gap-1 text-xs font-medium text-grey-3">
              <span>See all</span>
              <FontAwesomeIcon icon={faChevronRight} />
            </a>
          </div>
          <button
            type="button"
            className="preflight preflight-button flex min-w-4 items-center justify-end gap-1 text-xs font-medium text-grey-3"
            onClick={() => setExpand(false)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span className="sr-only">Collapse</span>
          </button>
        </div>
        <ul className="preflight preflight-ul customScrollBar light flex grow flex-col pb-4">
          {list.map(({ _id, animal, profileImage, slug: habitatSlug, zoo: { slug: zooSlug, name: zooName }, liveTalk }) => (
            <li key={_id}>
              <Card
                image={profileImage}
                animal={animal}
                habitatSlug={habitatSlug}
                zoo={zooName}
                zooSlug={zooSlug}
                liveTalk={liveTalk}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LiveChannelsBar;
